import {
  Box,
  CircularProgress,
  DialogActions as MuiDialogActions,
  Paper,
  Typography,
} from '@mui/material';

import {
  CustomButton,
  DialogBody,
  DialogContainer,
  DialogTitle,
} from '../../../../common/components';
import { formatDateTime } from '../../utils/puntajeFormatters';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const getDueLabel = (number) => {
  if (number === 1) return '1er vencimiento';
  if (number === 2) return '2do vencimiento';
  if (number === 3) return '3er vencimiento';
  return '-';
};

/**
 * Resuelve la etiqueta de cuotas del cupón.
 * - fee_number1 === 0 → Adecuación
 * - is_score → Puntaje
 * - fee_number2 existe y no es 0 → "Cuotas: X y Y"
 * - solo fee_number1 → "Cuota: X"
 */
const getCouponFeeLabel = (detail) => {
  if (!detail) return '-';
  const { fee_number1, fee_number2, is_score } = detail;

  if (fee_number1 === 0) return 'Adecuación';
  if (is_score) return 'Puntaje';
  if (fee_number2 && fee_number2 !== 0) {
    return `${fee_number1} y ${fee_number2}`;
  }
  if (fee_number1) return `${fee_number1}`;
  return '-';
};

const getCouponFeePrefix = (detail) => {
  if (!detail) return 'Cuota';
  const { fee_number1, fee_number2, is_score } = detail;
  if (fee_number1 === 0 || is_score) return 'Cuota';
  if (fee_number2 && fee_number2 !== 0) return 'Cuotas';
  return 'Cuota';
};

/**
 * Dialog que muestra el detalle de un cupón desde la tabla de movimientos de fondo.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Object|null} props.couponDetail - Datos del cupón devueltos por el backend.
 * @param {boolean} props.isLoading
 * @param {Object|null} props.error
 */
const CouponDetailDialog = ({ open, onClose, couponDetail, isLoading, error }) => (
  <DialogContainer open={open} onClose={onClose} size="small" minHeight="auto">
    <DialogTitle>Detalle del Cupón</DialogTitle>

    <DialogBody>
      {isLoading ? (
        <Box
          sx={{ minHeight: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body1">No se pudo obtener el detalle del cupón.</Typography>
        </Paper>
      ) : !couponDetail ? (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body1">Sin datos disponibles.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="h6">Cupón {couponDetail.code_number}</Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Typography variant="body2">
              {getCouponFeePrefix(couponDetail)}: <strong>{getCouponFeeLabel(couponDetail)}</strong>
            </Typography>
            <Typography variant="body2">
              Período: <strong>{couponDetail.year ?? '-'}/{String(couponDetail.month ?? '').padStart(2, '0')}</strong>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Typography variant="body2">
              Fecha de cobro: <strong>{formatDateTime(couponDetail.cancellation_date)}</strong>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Typography variant="body2">
              Vencimiento aplicado:{' '}
              <strong>
                {couponDetail.applied_due_number
                  ? `${getDueLabel(couponDetail.applied_due_number)} — ${formatDateTime(couponDetail.applied_due_date, false)}`
                  : '-'}
              </strong>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Typography variant="body2">
              Monto vencimiento:{' '}
              <strong>
                {couponDetail.applied_due_amount != null
                  ? formatCurrency(couponDetail.applied_due_amount)
                  : '-'}
              </strong>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Typography variant="body2">
              Contrato: <strong>{couponDetail.contract_number || '-'}</strong>
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2">
              Suscriptores:{' '}
              <strong>{couponDetail.subscriber_names?.join(', ') || '-'}</strong>
            </Typography>
          </Box>
        </Box>
      )}
    </DialogBody>

    <MuiDialogActions sx={{ px: 3, pb: 3 }}>
      <CustomButton variant="outlined" onClick={onClose}>
        Cerrar
      </CustomButton>
    </MuiDialogActions>
  </DialogContainer>
);

export default CouponDetailDialog;
